import { Badge } from './ui/badge';

// Foundation Spec F.2 — 9-label enum. Keep in sync with
// /app/backend/ai/structured_output.py PAC_LABELS. Distinct, accessible
// colors per label so a coach can scan evidence strength at a glance.
const LABEL_STYLES = {
  FACT: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  RULE: 'bg-blue-100 text-blue-800 border-blue-300',
  RESEARCH: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  'PAC STANDARD': 'bg-orange-100 text-orange-800 border-orange-300',
  'COACHING PRINCIPLE': 'bg-amber-100 text-amber-800 border-amber-300',
  OBSERVATION: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  INFERENCE: 'bg-purple-100 text-purple-800 border-purple-300',
  RECOMMENDATION: 'bg-pink-100 text-pink-800 border-pink-300',
  UNCERTAINTY: 'bg-gray-200 text-gray-700 border-gray-400',
};

export const PacLabelChip = ({ label }) => (
  <Badge
    variant="outline"
    className={`${LABEL_STYLES[label] || LABEL_STYLES.UNCERTAINTY} font-semibold text-[10px] uppercase tracking-wide`}
    data-testid={`pac-label-chip-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}
  >
    {label}
  </Badge>
);

// Renders a structured pac_reply (Phase 2). Returns null if `structured` is
// missing/malformed so callers can safely render this unconditionally and
// fall back to their own free-form text rendering.
export const PacReplyView = ({ structured }) => {
  if (!structured || !Array.isArray(structured.paragraphs) || structured.paragraphs.length === 0) {
    return null;
  }
  const confidencePct = Math.round((structured.confidence ?? 0) * 100);
  return (
    <div className="space-y-2" data-testid="pac-reply-structured">
      {structured.paragraphs.map((p, i) => (
        <div key={i} className="space-y-1" data-testid={`pac-reply-paragraph-${i}`}>
          <PacLabelChip label={p.label} />
          <p className="text-sm whitespace-pre-wrap mt-1">{p.text}</p>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2 pt-2 mt-2 border-t text-xs text-muted-foreground">
        {structured.citations?.length > 0 && (
          <span data-testid="pac-reply-citations" className="flex flex-wrap items-center gap-1">
            Sources:{' '}
            {structured.citations.map((c, i) => {
              const isToolSourced = /^[a-z_]+\(.*\)$/.test(c.ref || '');
              const isKbSourced = /^PAC-KB-[A-Z-]+\d+ v[\d.]+$/.test(c.ref || '');
              return (
                <span key={i} className="inline-flex items-center gap-1" data-testid={`pac-reply-citation-${i}`}>
                  {i > 0 && ', '}
                  {c.ref}
                  {isToolSourced && (
                    <Badge
                      variant="outline"
                      className="bg-sky-100 text-sky-800 border-sky-300 text-[9px] px-1 py-0"
                      data-testid={`pac-reply-citation-live-badge-${i}`}
                    >
                      live data
                    </Badge>
                  )}
                  {isKbSourced && (
                    <Badge
                      variant="outline"
                      className="bg-violet-100 text-violet-800 border-violet-300 text-[9px] px-1 py-0"
                      data-testid={`pac-reply-citation-kb-badge-${i}`}
                    >
                      knowledge base
                    </Badge>
                  )}
                </span>
              );
            })}
          </span>
        )}
        <span data-testid="pac-reply-confidence">Confidence: {confidencePct}%</span>
        {structured.action_state && (
          <Badge variant="secondary" data-testid="pac-reply-action-state">
            {structured.action_state}
          </Badge>
        )}
      </div>
    </div>
  );
};
